import MarkdownIt from 'markdown-it';
import RegExpUtil from './RegExpUtil';
import React from 'react';
import ColorUtil from './ColorUtil';

const markdownRenderer = new MarkdownIt({
  breaks: true,
  linkify: true,
  typographer: true,
});

const mentionsHighlighedText = markdown => RegExpUtil.replacePattern(markdown, /@\[([^\]]+)\]/igm, (match) => `**@${match}**`);

export default class StringUtil {
  static capitalizeFirstLetter = (s) => {
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  /** Converts a camelCase or PascalCase string to snake_case. */
  static toSnakeCase = (value) => {
    if (typeof value !== 'string') return '';

    // Normalize "DubBot" anywhere to "dubbot" (case-insensitive, all occurrences)
    value = value.replace(/dubbot/gi, 'dubbot');

    // Convert camelCase or PascalCase to snake_case
    return value
      .replace(/([A-Z])/g, '_$1')
      .replace(/^_+/, '') // remove leading underscores from PascalCase
      .toLowerCase();
  }

  /**
   * Safely trims a string value. Returns undefined for nullish inputs.
   * If s is not a string but is defined, coerces to string and trims.
   * @param {string|null|undefined} s
   * @returns {string|undefined}
   */
  static trim = (s) => {
    if (s == null) return undefined;
    return typeof s === 'string' ? s.trim() : String(s).trim();
  }

  static markdownToHtml = (markdown) => !markdown ? '' : markdownRenderer.render(mentionsHighlighedText(markdown));

  // HACK: used when we lose track of whether a string is a BSON Object ID or not
  static isObjectId = id => id?.length === 24 && id.match(/^[0-9a-f]{24}$/);

  static valueToString = (v) => (v != null ? String(v) : '');

  static truncate = (str, length = 100) => {
    if (!str || str.length <= length) return str;

    const ellipsis = "...";
    const allowed = length - ellipsis.length;

    // Split allowed characters roughly between the start and end of the string.
    const frontChars = Math.ceil(allowed / 2);
    const backChars = Math.floor(allowed / 2);

    return str.substring(0, frontChars) + ellipsis + str.substring(str.length - backChars);
  }

  static sliceString = (str, numChars) => {
    if (!str || !numChars) return str;
    const numCharsToShow = Math.max(numChars, 0);
    return str.slice(0, numCharsToShow);
  }

  static newlinesToHtmlBreaks = (text) => {
    if (!text) return text;

    // If it's already an array (from ColorUtil.addInlineColorPreviews), process each string element
    if (Array.isArray(text)) {
      const result = [];
      for (let i = 0; i < text.length; i++) {
        const item = text[i];
        if (typeof item === 'string') {
          // Process strings for newlines
          const processed = this.newlinesToHtmlBreaks(item);
          if (Array.isArray(processed)) {
            result.push(...processed);
          } else {
            result.push(processed);
          }
        } else {
          // Keep React components as-is
          result.push(item);
        }
      }
      return result;
    }

    // Handle plain strings
    const parts = text.split('\n');
    if (parts.length === 1) return text;

    const result = [];
    for (let i = 0; i < parts.length; i++) {
      if (i > 0) {
        result.push(React.createElement('br', { key: `br-${i}` }));
      }
      if (parts[i]) {
        result.push(parts[i]);
      }
    }
    return result;
  }

  static convertFailureSummaryToList = (text) => {
    if (!text) return text;

    // Find all "Fix ... of the following:" patterns
    const fixPattern = /(Fix\s+[^:]+:\s*)(.*?)(?=Fix\s+[^:]+:\s*|$)/gis;
    const matches = [...text.matchAll(fixPattern)];

    if (matches.length === 0) return text;

    const result = [];

    matches.forEach((match, matchIndex) => {
      const prefix = match[1]; // "Fix ... of the following:"
      const listContent = match[2]; // The content that should become a list

      // Split on newlines and create list items
      const listItems = listContent
        .split(/\n/)
        .map(item => item.trim())
        .filter(item => item.length > 0);

      if (listItems.length === 0) {
        result.push(
          React.createElement(React.Fragment, { key: `prefix-${matchIndex}` }, prefix)
        );
        return;
      }

      result.push(
        React.createElement(React.Fragment, { key: `prefix-${matchIndex}` }, prefix)
      );
      result.push(
        React.createElement('ul', { key: `accessibility-list-${matchIndex}` },
          listItems.map((item, index) =>
            React.createElement('li', { key: `list-item-${matchIndex}-${index}` }, item)
          )
        )
      );
    });

    return result;
  }

  static convertFailureSummaryToListWithColorSupport = (text) => {
    if (!text) return text;

    // First convert to lists, then apply color previews
    const listContent = this.convertFailureSummaryToList(text);

    // If no patterns found, just apply color previews directly
    if (listContent === text) {
      const colorPreviewResult = ColorUtil.addInlineColorPreviews(text);
      // Add keys to React elements in the array if it's an array
      if (Array.isArray(colorPreviewResult)) {
        return colorPreviewResult.map((item, idx) => {
          if (React.isValidElement(item)) {
            return React.cloneElement(item, { key: `color-preview-${idx}` });
          }
          return item;
        });
      }
      return colorPreviewResult;
    }

    // Helper function to add keys to arrays returned by addInlineColorPreviews
    const addKeysToColorPreviewArray = (arr, baseKey) => {
      if (!Array.isArray(arr)) return arr;
      return arr.map((item, idx) => {
        if (React.isValidElement(item)) {
          return React.cloneElement(item, { key: `${baseKey}-color-${idx}` });
        }
        return item;
      });
    };

    // Apply color previews to each list item
    const processListItems = (items) => {
      return items.map((item, index) => {
        // Preserve the original key if it exists
        const originalKey = React.isValidElement(item) ? item.key : null;
        const itemKey = originalKey || `item-${index}`;

        if (typeof item === 'string') {
          const colorPreviewResult = ColorUtil.addInlineColorPreviews(item);
          const withKeys = addKeysToColorPreviewArray(colorPreviewResult, itemKey);
          // Wrap in Fragment to preserve the key structure
          return React.createElement(React.Fragment, { key: itemKey }, withKeys);
        } else if (React.isValidElement(item) && item.type === 'ul') {
          const newChildren = React.Children.map(item.props.children, (child, childIndex) => {
            if (React.isValidElement(child) && child.type === 'li') {
              const liContent = child.props.children;
              // Preserve the original li key
              const liKey = child.key || `list-item-${childIndex}`;
              if (typeof liContent === 'string') {
                const colorPreviewResult = ColorUtil.addInlineColorPreviews(liContent);
                const withKeys = addKeysToColorPreviewArray(colorPreviewResult, liKey);
                return React.cloneElement(child, {
                  key: liKey,
                  children: withKeys
                });
              }
            }
            return child;
          });
          return React.cloneElement(item, {
            key: itemKey, // Use preserved key instead of hardcoded
            children: newChildren
          });
        } else if (React.isValidElement(item)) {
          // Preserve other React elements as-is with their keys
          return item;
        }
        return item;
      });
    };

    return processListItems(listContent);
  }
}
